function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeTransactions(payload) {
  if (Array.isArray(payload?.transactions)) {
    return payload.transactions;
  }

  if (payload?.transactions && typeof payload.transactions === 'object') {
    return Object.values(payload.transactions);
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function buildProfileName(transaction) {
  const profile = transaction?.profile || {};
  const firstName = pickFirst(profile.firstName, profile.first_name);
  const lastName = pickFirst(profile.lastName, profile.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(profile.email, profile.uid, profile.id) || '-';
}

function buildAssignedToName(transaction) {
  const assigned = transaction?.assigned || {};
  const firstName = pickFirst(assigned.firstName, assigned.first_name);
  const lastName = pickFirst(assigned.lastName, assigned.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(assigned.email, assigned.uid, assigned.id) || '-';
}

function buildMoneyAmountMinor(money) {
  if (!money || typeof money !== 'object') {
    return '';
  }

  const rawAmount = money.amount;
  if (rawAmount === undefined || rawAmount === null || String(rawAmount).trim() === '') {
    return '';
  }

  return String(rawAmount).trim();
}

function normalizeTransactionSortBy(value) {
  const normalized = String(value || '').trim();
  return ['created', 'amount'].includes(normalized) ? normalized : 'created';
}

function normalizeTransactionSortOrder(value) {
  return String(value || '').trim().toLowerCase() === 'asc' ? 'asc' : 'desc';
}

export function mapTransactionsResponseToDto(payload) {
  return normalizeTransactions(payload).map((transaction, index) => ({
    key: String(pickFirst(transaction.uid, transaction.reference, `transaction-${index}`)),
    id: String(pickFirst(transaction.uid) || '').trim(),
    transactionNumber: pickFirst(transaction.transactionNumber, transaction.transaction_number) || '-',
    profileId: String(pickFirst(transaction?.profile?.uid, transaction?.profile?.id) || '').trim(),
    profileName: buildProfileName(transaction),
    type: pickFirst(transaction.type) || '-',
    methodId: pickFirst(transaction.methodId, transaction.method_id) || '-',
    reference: pickFirst(transaction.reference) || '-',
    amountMinor: buildMoneyAmountMinor(transaction.money),
    currency: pickFirst(transaction?.money?.currency?.code, transaction?.money?.currency) || '-',
    status: pickFirst(transaction.status) || '-',
    assignedToId: String(pickFirst(transaction?.assigned?.uid, transaction?.assigned?.id) || '').trim(),
    assignedToName: buildAssignedToName(transaction),
    accountLogin: pickFirst(
      transaction?.account?.login,
      transaction?.accountIdOpt?.login,
    ) || '-',
    created: pickFirst(transaction.created, transaction.createdAt, transaction.created_at),
    modified: pickFirst(transaction.modified, transaction.updatedAt, transaction.updated_at),
    source: transaction,
  }));
}

export function buildTransactionsRequestDto(type, pagination = {}, filters = {}) {
  const normalizedType = String(type || '').trim().toLowerCase();
  const normalizedStatus = String(filters?.status || '').trim().toLowerCase();
  const normalizedMethod = String(filters?.method || '').trim();
  const normalizedAccount = String(filters?.account || '').trim();
  const normalizedTransactionNumber = String(filters?.transactionNumber || '').trim();
  const normalizedReference = String(filters?.reference || '').trim();
  const normalizedProfile = String(filters?.profile || '').trim();
  const normalizedProfileName = String(filters?.profileName || '').trim();
  const normalizedProfileEmail = String(filters?.profileEmail || '').trim();
  const dto = {
    sortBy: normalizeTransactionSortBy(filters?.sortBy),
    sortOrder: normalizeTransactionSortOrder(filters?.sortOrder),
    limit: Number.isInteger(pagination?.limit) && pagination.limit > 0 ? pagination.limit : 20,
  };

  if (Number.isInteger(pagination?.offset) && pagination.offset > 0) {
    dto.offset = pagination.offset;
  }

  if (normalizedType) {
    dto.filterBy = [{ field: 'type', filter: normalizedType }];
  }

  if (normalizedStatus) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'status', filter: normalizedStatus },
    ];
  }

  if (normalizedMethod) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'methodId', filter: normalizedMethod },
    ];
  }

  if (normalizedProfile) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'profile', filter: normalizedProfile },
    ];
  }

  if (normalizedAccount) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'account', filter: normalizedAccount },
    ];
  }

  if (normalizedTransactionNumber) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'transactionNumber', filter: normalizedTransactionNumber },
    ];
  }

  if (normalizedReference) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'reference', filter: normalizedReference },
    ];
  }

  if (normalizedProfileName) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'profileName', filter: normalizedProfileName },
    ];
  }

  if (normalizedProfileEmail) {
    dto.filterBy = [
      ...(dto.filterBy || []),
      { field: 'profileEmail', filter: normalizedProfileEmail },
    ];
  }

  if (Array.isArray(filters?.countriesOnly) && filters.countriesOnly.length > 0) {
    dto.countriesOnly = filters.countriesOnly
      .map((item) => String(item || '').trim().toUpperCase())
      .filter(Boolean);
  }

  return dto;
}
